import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function BookingInquiryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tripType   = searchParams.get("tripType")   || "One way";
  const from       = searchParams.get("from")        || "—";
  const to         = searchParams.get("to")          || "—";
  const date       = searchParams.get("date")        || "—";
  const returnDate = searchParams.get("returnDate")  || "";
  const cabinClass = searchParams.get("class")       || "Economy";
  const pax        = searchParams.get("pax")         || "1";
  const from2      = searchParams.get("from2")       || "";
  const to2        = searchParams.get("to2")         || "";
  const date2      = searchParams.get("date2")       || "";

  const [name, setName]       = useState("");
  const [phone, setPhone]     = useState("");
  const [email, setEmail]     = useState("");
  const [city, setCity]       = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  const formatDate = (d: string) => {
    if (!d || d === "—") return "—";
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!phone.trim()) { setError("Please enter your phone number."); return; }
    if (!city.trim()) { setError("Please enter your city."); return; }
    if (message.length > 500) { setError("Message must be 500 characters or fewer."); return; }

    setError("");
    setLoading(true);

    const body = new URLSearchParams({
      "Your Name":      name,
      "Phone No":       `+92${phone}`,
      "Email":          email || "Not provided",
      "Your City":      city,
      "Message":        message || "—",
      "Trip Type":      tripType,
      "Cabin Class":    cabinClass,
      "Adults":         pax,
      "Leg 1 From":     from,
      "Leg 1 To":       to,
      "Leg 1 Date":     formatDate(date),
      ...(tripType === "Return" && returnDate
        ? { "Return Date": formatDate(returnDate) }
        : {}),
      ...(tripType === "Multi City" && from2
        ? { "Leg 2 From": from2, "Leg 2 To": to2, "Leg 2 Date": formatDate(date2) }
        : {}),
    });

    try {
      await fetch("https://readdy.ai/api/form/d723serk1jkj467r5r5g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          Back
        </button>
        <img
          src="https://storage.readdy-site.link/project_files/6f8e2f56-80e7-4d90-9ea4-7823017b65ac/8001b0b9-8322-4e8e-a8ae-9e1b8da726a7_Untitled-design-13-2.png?v=4559551c5eed3b8ee3339af394cf2f6c"
          alt="flightfare.pk"
          className="h-12 w-auto object-contain"
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Heading */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-bold text-gray-900">Customer Support</h1>
          <p className="text-sm text-gray-500 mt-1.5">
            Send your flight inquiry to flightfare.pk for a quick response and the best pricing options.
          </p>
        </div>

        {submitted ? (
          <div className="max-w-md mx-auto text-center bg-white rounded-xl border border-gray-200 p-10">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Inquiry Sent!</h2>
            <p className="text-sm text-gray-500 mb-6">
              Our team will get back to you as soon as possible with the best available fares.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-6 py-2.5 rounded cursor-pointer transition-colors whitespace-nowrap"
            >
              Back to Home
            </button>
          </div>
        ) : (
          <div className="flex flex-col md:grid md:grid-cols-5 gap-6 md:gap-8 items-start">
            {/* ── Left: Flight Summary ── */}
            <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-800 text-white text-xs font-bold tracking-widest px-5 py-3 uppercase">
                Flight
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { label: "Class",     value: cabinClass },
                  { label: "Adult",     value: pax },
                  { label: "Route",     value: tripType },
                  { label: "From",      value: from },
                  { label: "To",        value: to },
                  { label: "Departure", value: formatDate(date) },
                  ...(tripType === "Return" && returnDate
                    ? [{ label: "Return", value: formatDate(returnDate) }]
                    : []),
                  ...(tripType === "Multi City" && from2
                    ? [
                        { label: "Leg 2 From", value: from2 },
                        { label: "Leg 2 To",   value: to2 },
                        { label: "Leg 2 Date", value: formatDate(date2) },
                      ]
                    : []),
                ].map(row => (
                  <div key={row.label} className="flex items-center px-5 py-3 gap-4">
                    <span className="text-xs text-gray-400 w-24 flex-shrink-0">{row.label}</span>
                    <span className="text-sm text-gray-800 font-medium">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: Customer Form ── */}
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="md:col-span-3 bg-white rounded-xl border border-gray-200 p-5 md:p-7 flex flex-col gap-5"
            >
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Phone No. <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center border border-gray-300 rounded-md overflow-hidden focus-within:border-amber-400 transition-colors">
                  <span className="flex items-center gap-1 px-3 py-2.5 bg-gray-50 border-r border-gray-300 text-sm text-gray-600 whitespace-nowrap select-none">
                    <i className="ri-arrow-down-s-line text-xs"></i>
                    +92
                  </span>
                  <input
                    name="phone"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="3001234567"
                    maxLength={10}
                    className="flex-1 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  E-mail <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* City */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Your City <span className="text-red-500">*</span>
                </label>
                <input
                  name="city"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="e.g. Lahore"
                  className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Message or Comments <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <textarea
                  name="message"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  placeholder="Any special requests or questions..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/500</p>
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-md cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-fill text-base"></i>
                {loading ? "Sending..." : "SEND NOW"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
